import React from "react";
import { Image, View, Text } from "react-native";
import { StyleSheet } from "@/utils/appStyleSheet";
import { FontFamilies } from "@/constants/Typography";
import { Colors } from "@/constants/Colors";
import { BASE_URL } from "@/constants/Configs";
import {
  ClaimProgressBar,
  ClaimStage,
  getClaimStage,
} from "@/components/claims/ClaimProgressBar";
import { ClaimHistoryItem } from "@/services/claims";

const STATUS_CONFIG: Record<
  ClaimStage,
  { label: string; backgroundColor: string; dotColor: string; textColor: string; borderColor: string }
> = {
  inQueue: {
    label: "In queue",
    backgroundColor: "#F4F5F6",
    dotColor: "#333333",
    textColor: "#333333",
    borderColor: "transparent",
  },
  mailed: {
    label: "Mailed",
    backgroundColor: "#FDE7D1",
    dotColor: "#A4160E",
    textColor: "#A4160E",
    borderColor: "transparent",
  },
  received: {
    label: "Received",
    backgroundColor: "#FFFACC",
    dotColor: "#B89600",
    textColor: "#B89600",
    borderColor: "transparent",
  },
  reviewing: {
    label: "Reviewing",
    backgroundColor: "#E5FCFF",
    dotColor: "#2EA5FA",
    textColor: "#2EA5FA",
    borderColor: "transparent",
  },
  decision: {
    label: "Decision",
    backgroundColor: "#F8FFE0",
    dotColor: "#5E990A",
    textColor: "#5E990A",
    borderColor: "transparent",
  },
};

const formatPayout = (payout: number) => {
  const dollars = payout / 100;
  return `Up to $${dollars % 1 === 0 ? dollars.toFixed(0) : dollars.toFixed(2)}`;
};

const formatDate = (dateString: string, format: "long" | "short" = "long") => {
  const date = new Date(dateString);
  if (format === "short") {
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const getFooterMessage = (stage: ClaimStage, item: ClaimHistoryItem) => {
  switch (stage) {
    case "inQueue":
      return "We have your information and will mail it by the filing deadline";
    case "mailed":
      return item.mailedAt
        ? `We mailed in your claim form on ${formatDate(item.mailedAt)}`
        : "We have your information and will mail it by the filing deadline";
    case "received": {
      if (!item.mailedAt) return "Your claim form has been received.";
      const delivered = new Date(item.mailedAt);
      delivered.setDate(delivered.getDate() + 10);
      return `Claim form was delivered around ${formatDate(delivered.toISOString(), "short")}. Reviews start a month after filing closes.`;
    }
    case "reviewing":
      return "The administrator is reviewing claims. This step typically takes 3 to 6 months.";
    case "decision":
      return "The administrator sent out claim decisions directly to you.";
  }
};

interface ClaimHistoryCardProps {
  item: ClaimHistoryItem;
}

export function ClaimHistoryCard({ item }: ClaimHistoryCardProps) {
  const stage = getClaimStage({
    createdAt: item.createdAt,
    mailedAt: item.mailedAt,
    dueAt: item.dueAt,
    claimed: item.claimed,
  });
  const status = STATUS_CONFIG[stage];
  const footerMessage = getFooterMessage(stage, item);

  return (
    <View style={styles.card}>
      {/* Header */}
      <View style={styles.header}>
        {item.brandImage ? (
          <View style={styles.imageContainer}>
            <Image
              source={{ uri: BASE_URL + item.brandImage }}
              style={styles.image}
              resizeMode="cover"
            />
          </View>
        ) : null}

        <View style={styles.titleSection}>
          <Text style={styles.suitName} numberOfLines={2}>
            {item.name}
          </Text>
          <Text style={styles.payout}>{formatPayout(item.payout)}</Text>
        </View>

        <View
          style={[
            styles.badge,
            {
              backgroundColor: status.backgroundColor,
              borderColor: status.borderColor,
            },
          ]}
        >
          <View
            style={[styles.badgeDot, { backgroundColor: status.dotColor }]}
          />
          <Text style={[styles.badgeText, { color: status.textColor }]}>
            {status.label}
          </Text>
        </View>
      </View>

      {/* Progress */}
      <View style={styles.progressSection}>
        <ClaimProgressBar currentStage={stage} />
      </View>

      {/* Footer */}
      <View style={styles.footer}>
        <View style={styles.infoIcon}>
          <Text style={styles.infoIconText}>i</Text>
        </View>
        <Text style={styles.footerText}>{footerMessage}</Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    marginHorizontal: 16,
    marginVertical: 8,
    borderWidth: 1,
    borderColor: "#EBEBEB",
    overflow: "hidden",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    padding: 16,
    gap: 12,
  },
  imageContainer: {
    width: 64,
    height: 64,
    borderRadius: 14,
    backgroundColor: "#F7F8FA",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
  },
  image: {
    width: "100%",
    height: "100%",
    borderRadius: 14,
    borderWidth: 1,
    borderColor: Colors.lightGrey2,
  },
  titleSection: {
    flex: 1,
    gap: 2,
  },
  suitName: {
    fontSize: 14,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#161D14",
  },
  payout: {
    fontSize: 20,
    fontFamily: FontFamilies.InterTight.SemiBold,
    color: Colors.text,
  },
  badge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    alignSelf: "flex-start",
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 20,
    borderWidth: 1,
  },
  badgeDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  badgeText: {
    fontSize: 13,
    fontFamily: FontFamilies.Inter.Medium,
  },
  progressSection: {
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  footer: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: "#EBEBEB",
  },
  infoIcon: {
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: "#E5E7EB",
    justifyContent: "center",
    alignItems: "center",
    marginTop: 1,
  },
  infoIconText: {
    fontSize: 11,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#9CA3AF",
    lineHeight: 13,
  },
  footerText: {
    flex: 1,
    fontSize: 13,
    fontFamily: FontFamilies.InterTight.Regular,
    color: "#374151",
    lineHeight: 18,
  },
});
